/**
 * FeatureFlagsPage — Platform-Admin-only control panel for runtime feature flags.
 *
 * Route: /flags   (registered in App.jsx behind <RoleGuard exactRole="Platform Admin">)
 *
 * Platform-Admin-only (not Super Admin): /flags is the single gatekeeper for
 * cluster-wide backend Runtime flags (rendered as informational, restart-
 * required rows), which are platform-scoped — so only Platform Admin governs
 * them. The browser-local toggles (canvas/redesign/nav/AI) live here too.
 *
 * Reads the flag registry from utils/featureFlags.js (source of truth) and
 * renders one row per flag with:
 *   - current resolved value (what the app sees right now)
 *   - raw stored value (unset / 'true' / 'false' / other)
 *   - ON / OFF / Default tri-state buttons
 *
 * Why tri-state: several flags (codeTab) default ON when unset, others
 * (canvasV2, irPipeline, containerModel) default OFF. "Reset to default"
 * clears the stored override so the accessor's default rule wins again —
 * distinct from "force OFF" which writes `false` explicitly.
 *
 * Why a full page reload after each change: a few flag sites (notably the
 * /canvas-v2 route registration in App.jsx) read the flag at component
 * mount, not on every render. Reloading is the safest way to guarantee
 * the toggle takes effect everywhere, including gated routes, lazy
 * imports, and persisted canvas state.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Flag } from 'lucide-react';
import { FLAG_REGISTRY, setFeatureFlag, getRawFlag } from '../utils/featureFlags';
import {
  getPlatformRuntimeFlags,
  setPlatformRuntimeFlag,
  clearPlatformRuntimeFlag,
} from '../services/api';
import PageHeader from '../components/common/PageHeader';
import PageContainer from '../components/common/PageContainer';
/* G94 — re-skin chrome with v2. */
import {
  V2PageShell,
  Button as V2Button,
  Pill as V2Pill,
} from '../components/v2';

function Pill({ tone = 'neutral', children }) {
  const palette = {
    on:      { bg: '#dcfce7', fg: '#14532d', border: '#86efac' },
    off:     { bg: '#fef2f2', fg: '#7f1d1d', border: '#fca5a5' },
    default: { bg: '#f1f5f9', fg: '#334155', border: '#cbd5e1' },
    neutral: { bg: '#f8fafc', fg: '#475569', border: '#e2e8f0' },
  }[tone];
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', fontSize: 11, fontWeight: 600,
      color: palette.fg, background: palette.bg,
      border: `1px solid ${palette.border}`, borderRadius: 999,
    }}>{children}</span>
  );
}

/**
 * Backend-managed STRING flag (e.g. the OAuth public base URL) — a value flag,
 * not a boolean. Renders a text field + Save + Reset, persisting the value via the
 * same Platform-Admin runtime-flag endpoint the boolean toggles use
 * (`onServerToggle(key, value)` where a null value clears the override). Kept as its
 * own component so it can own the input draft state without adding hooks to the
 * boolean FlagRow.
 */
function ServerTextFlagRow({ flag, value, onServerToggle, serverBusy }) {
  const [draft, setDraft] = useState(value || '');
  // Re-sync the field when the persisted value changes (initial fetch, or after a
  // save/clear round-trip) so the input always reflects what's actually stored.
  useEffect(() => { setDraft(value || ''); }, [value]);

  const liveReload = /immediately|read live/i.test(flag.restartImpact || '');
  const suffix = liveReload ? 'live' : 'pending restart';
  const hasOverride = typeof value === 'string' && value.trim().length > 0;
  const draftTrimmed = draft.trim();
  const dirty = draftTrimmed !== (value || '').trim();
  const canSave = !serverBusy && dirty && draftTrimmed.length > 0;

  return (
    <div style={{
      padding: 20, border: '1px solid #e2e8f0', borderRadius: 10,
      background: '#fafaf9', marginBottom: 12, borderLeft: '3px solid #94a3b8',
    }}>
      <div style={{ marginBottom: 8 }}>
        <div style={{
          fontSize: 15, fontWeight: 600, color: '#0f172a', marginBottom: 4,
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        }}>
          {flag.label}
          <Pill tone="neutral">backend-managed</Pill>
          {hasOverride
            ? <Pill tone="on">override set ({suffix})</Pill>
            : <Pill tone="default">using env default</Pill>}
        </div>
        <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.55 }}>
          {flag.description}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
        <input
          type="text"
          value={draft}
          placeholder={flag.placeholder || ''}
          disabled={serverBusy}
          onChange={(e) => setDraft(e.target.value)}
          style={{
            flex: 1, padding: '8px 10px', fontSize: 13,
            fontFamily: 'SFMono-Regular, Menlo, monospace',
            border: '1px solid #cbd5e1', borderRadius: 6,
            color: '#0f172a', background: '#fff',
          }}
        />
        <button
          disabled={!canSave}
          onClick={() => onServerToggle(flag.flagKey, draftTrimmed)}
          style={{
            padding: '8px 14px', fontSize: 12, fontWeight: 600, color: '#fff',
            background: canSave ? '#2563eb' : '#93c5fd',
            border: '1px solid #2563eb', borderRadius: 6,
            cursor: canSave ? 'pointer' : 'not-allowed',
          }}
        >Save</button>
        <button
          disabled={serverBusy || !hasOverride}
          onClick={() => onServerToggle(flag.flagKey, null)}
          style={{
            padding: '8px 14px', fontSize: 12, fontWeight: 600, color: '#334155',
            background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 6,
            cursor: (serverBusy || !hasOverride) ? 'not-allowed' : 'pointer',
            opacity: (serverBusy || !hasOverride) ? 0.6 : 1,
          }}
        >Reset to default</button>
      </div>
      <div style={{
        marginTop: 10, fontSize: 11, color: '#475569',
        fontFamily: 'SFMono-Regular, Menlo, monospace',
      }}>
        property: <code style={{ background: '#fff', padding: '1px 6px',
          border: '1px solid #e2e8f0', borderRadius: 4 }}>
          {flag.propertyName || flag.flagKey}
        </code>
      </div>
      {flag.restartImpact && (
        <div style={{ marginTop: 6, fontSize: 11, color: '#92400e' }}>
          ⚠ {flag.restartImpact}{liveReload
            ? ' Saved here, applied to the running cluster immediately.'
            : ' Saved here, applied to the running cluster on next restart.'}
        </div>
      )}
    </div>
  );
}

function FlagRow({ flag, onChange, serverOverride, onServerToggle, serverBusy }) {
  // Backend-managed flags render informationally. Those marked serverToggle
  // (ID-7) additionally expose Platform-Admin ON/OFF/Default controls that
  // persist a server-side override (applied on the next restart) — a localStorage
  // button would be meaningless for a cluster-wide backend flag.
  if (flag.backendManaged) {
    // A string-valued backend flag (e.g. the OAuth public base URL) renders a text
    // field instead of an ON/OFF toggle.
    if (flag.serverText) {
      return (
        <ServerTextFlagRow
          flag={flag}
          value={serverOverride}
          onServerToggle={onServerToggle}
          serverBusy={serverBusy}
        />
      );
    }
    const isServerToggle = !!flag.serverToggle;
    // Read-live flags (their accessor hits the DB on every request) take effect the moment the
    // override is saved — no restart. Detect that from the descriptor's restartImpact so the pill
    // doesn't cry "pending restart" for a flag that's already live (false positive).
    const liveReload = /immediately|read live/i.test(flag.restartImpact || '');
    const overrideSuffix = liveReload ? 'live' : 'pending restart';
    // serverOverride: 'true' | 'false' | undefined (no override → env default).
    const overridePill = serverOverride === 'true'
      ? <Pill tone="on">override: ON ({overrideSuffix})</Pill>
      : serverOverride === 'false'
        ? <Pill tone="off">override: OFF ({overrideSuffix})</Pill>
        : <Pill tone="default">using env default</Pill>;

    const sbtn = (label, value, active) => (
      <button
        disabled={serverBusy}
        onClick={() => onServerToggle(flag.flagKey, value)}
        style={{
          padding: '6px 14px', fontSize: 12, fontWeight: 600,
          color: active ? '#fff' : '#334155',
          background: active ? '#2563eb' : '#f8fafc',
          border: `1px solid ${active ? '#2563eb' : '#cbd5e1'}`,
          borderRadius: 6, cursor: serverBusy ? 'wait' : 'pointer',
          opacity: serverBusy ? 0.6 : 1,
        }}
      >{label}</button>
    );

    return (
      <div style={{
        padding: 20, border: '1px solid #e2e8f0', borderRadius: 10,
        background: '#fafaf9', marginBottom: 12, borderLeft: '3px solid #94a3b8',
      }}>
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          gap: 16,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: 15, fontWeight: 600, color: '#0f172a', marginBottom: 4,
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            }}>
              {flag.label}
              <Pill tone="neutral">backend-managed</Pill>
              {isServerToggle ? overridePill : (flag.defaultValue && (
                <Pill tone="default">default <code>{flag.defaultValue}</code></Pill>
              ))}
            </div>
            <div style={{
              fontSize: 12, color: '#475569', lineHeight: 1.55,
              fontFamily: 'system-ui, sans-serif',
            }}>
              {flag.description}
            </div>
          </div>
          {isServerToggle && (
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              {sbtn('Save ON', 'true', serverOverride === 'true')}
              {sbtn('Save OFF', 'false', serverOverride === 'false')}
              {sbtn('Default', null, serverOverride === undefined)}
            </div>
          )}
        </div>
        <div style={{
          marginTop: 10, fontSize: 11, color: '#475569',
          fontFamily: 'SFMono-Regular, Menlo, monospace',
        }}>
          property: <code style={{ background: '#fff', padding: '1px 6px',
            border: '1px solid #e2e8f0', borderRadius: 4 }}>
            {flag.propertyName || flag.name}
          </code>
        </div>
        {flag.restartImpact && (
          <div style={{
            marginTop: 6, fontSize: 11, color: '#92400e',
            fontFamily: 'system-ui, sans-serif',
          }}>
            ⚠ {flag.restartImpact}{isServerToggle && (liveReload
              ? ' Saved here, applied to the running cluster immediately.'
              : ' Saved here, applied to the running cluster on next restart.')}
          </div>
        )}
      </div>
    );
  }

  // Standard localStorage-backed flag — original render.
  const resolved = flag.accessor();
  const raw = getRawFlag(flag.name);
  const isOverridden = raw !== null;

  const rawTone = raw === 'true' ? 'on' : raw === 'false' ? 'off' : 'default';
  const resolvedTone = resolved ? 'on' : 'off';

  const setTo = (value) => {
    setFeatureFlag(flag.name, value);
    onChange();
  };

  const btn = (label, onClick, active) => (
    <button
      onClick={onClick}
      style={{
        padding: '6px 14px', fontSize: 12, fontWeight: 600,
        color: active ? '#fff' : '#334155',
        background: active ? '#2563eb' : '#f8fafc',
        border: `1px solid ${active ? '#2563eb' : '#cbd5e1'}`,
        borderRadius: 6, cursor: 'pointer',
      }}
    >{label}</button>
  );

  return (
    <div style={{
      padding: 20, border: '1px solid #e2e8f0', borderRadius: 10,
      background: '#fff', marginBottom: 12,
    }}>
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 16, marginBottom: 8,
      }}>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: 15, fontWeight: 600, color: '#0f172a', marginBottom: 4,
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          }}>
            {flag.label}
            <Pill tone={resolvedTone}>
              Currently {resolved ? 'ON' : 'OFF'}
            </Pill>
            {isOverridden && (
              <Pill tone={rawTone}>
                override: {raw}
              </Pill>
            )}
            {!isOverridden && (
              <Pill tone="default">
                using default ({flag.defaultOn ? 'ON' : 'OFF'})
              </Pill>
            )}
          </div>
          <div style={{
            fontSize: 12, color: '#64748b', lineHeight: 1.55,
            fontFamily: 'system-ui, sans-serif',
          }}>
            {flag.description}
          </div>
          <div style={{
            marginTop: 8, fontSize: 11, color: '#94a3b8',
            fontFamily: 'SFMono-Regular, Menlo, monospace',
          }}>
            localStorage key: <code>features.{flag.name}</code>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {btn('Force ON',  () => setTo(true),  raw === 'true')}
          {btn('Force OFF', () => setTo(false), raw === 'false')}
          {btn('Default',   () => setTo(null),  raw === null)}
        </div>
      </div>
    </div>
  );
}

export default function FeatureFlagsPage() {
  // Re-render trigger — setFeatureFlag writes synchronously but we need
  // React to re-read the accessors after each toggle.
  const [tick, setTick] = useState(0);
  const bump = useCallback(() => setTick((t) => t + 1), []);

  // ID-7 — server-persisted overrides for cluster-wide backend Runtime flags
  // (Platform-Admin-only). Map of flagKey → 'true' | 'false'. Absent key = no
  // override (env default wins). Fetched on mount + after each toggle.
  const [serverOverrides, setServerOverrides] = useState({});
  const [serverBusy, setServerBusy] = useState(false);

  const refreshServerFlags = useCallback(async () => {
    try {
      const res = await getPlatformRuntimeFlags();
      const overrides = res?.data?.overrides || {};
      const next = {};
      for (const [k, v] of Object.entries(overrides)) {
        if (v && typeof v.value === 'string') next[k] = v.value;
      }
      setServerOverrides(next);
    } catch (_) {
      // PA-only endpoint; non-PA or transient errors leave overrides empty
      // (rows then just show "using env default"). No throw.
    }
  }, []);

  useEffect(() => { refreshServerFlags(); }, [refreshServerFlags]);

  const onServerToggle = useCallback(async (key, value) => {
    if (!key) return;
    setServerBusy(true);
    try {
      if (value === null) await clearPlatformRuntimeFlag(key);
      else await setPlatformRuntimeFlag(key, value);
      await refreshServerFlags();
    } catch (_) {
      // leave UI as-is; a failed write simply doesn't change the override pill
    } finally {
      setServerBusy(false);
    }
  }, [refreshServerFlags]);

  // Group rows by `flag.group` so the page renders one section per
  // category. Flags without a group fall into "Other" and surface at
  // the bottom — useful for legacy entries that pre-date the field.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const flag of FLAG_REGISTRY) {
      const g = flag.group || 'Other';
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(flag);
    }
    // Stable, intentional ordering: toggle-able groups first, then
    // backend-managed reference material at the bottom so operators don't
    // scroll past read-only entries to reach the things they can flip.
    const PREFERRED = ['Canvas & layout', 'Navigation', 'AI builders', 'Runtime'];
    const TRAILING  = ['Backend operations', 'Other'];
    const middle = [...map.keys()]
      .filter((k) => !PREFERRED.includes(k) && !TRAILING.includes(k))
      .sort();
    const sortedKeys = [
      ...PREFERRED.filter((k) => map.has(k)),
      ...middle,
      ...TRAILING.filter((k) => map.has(k)),
    ];
    return sortedKeys.map((k) => ({ group: k, flags: map.get(k) }));
  }, []);

  const reload = () => {
    window.location.reload();
  };

  const resetAll = () => {
    if (!window.confirm('Clear every feature-flag override? All flags will return to their built-in defaults.')) return;
    // Skip backend-managed entries — they have no localStorage key to
    // clear, and including them in the loop pretends we did something.
    for (const f of FLAG_REGISTRY) {
      if (f.backendManaged) continue;
      setFeatureFlag(f.name, null);
    }
    bump();
  };

  return (
    <V2PageShell
      title="Feature Flags"
      subtitle="Platform Admin only. Browser-local toggles (Canvas/Redesign/Nav/AI) apply on next page load and affect only your browser — use Reload app after flipping anything that affects routing. Backend Runtime flags are cluster-wide; those with Save ON/OFF controls persist a server override — read-live flags (marked 'live') take effect immediately, the rest apply on the next application restart."
      tags={<V2Pill tone="trust" size="sm">System</V2Pill>}
      actions={
        <>
          <V2Button variant="primary" size="sm" onClick={reload}>Reload app</V2Button>
          <V2Button variant="secondary" size="sm" onClick={resetAll}>Reset all to defaults</V2Button>
        </>
      }
    >
      <div className="flex items-center justify-end mb-4 text-[11px] text-v2-ink-muted font-mono-v2">
        {FLAG_REGISTRY.length} flags · refresh #{tick}
      </div>

      {grouped.map(({ group, flags }) => (
        <div key={group} style={{ marginBottom: 24 }}>
          <div style={{
            display: 'flex', alignItems: 'baseline', gap: 8,
            margin: '12px 0 10px',
            paddingBottom: 6,
            borderBottom: '1px solid #e2e8f0',
          }}>
            <h2 style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', margin: 0, letterSpacing: '0.02em' }}>
              {group}
            </h2>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>
              {flags.length} {flags.length === 1 ? 'flag' : 'flags'}
            </span>
          </div>
          {flags.map((flag) => (
            <FlagRow
              key={flag.name}
              flag={flag}
              onChange={bump}
              serverOverride={flag.flagKey ? serverOverrides[flag.flagKey] : undefined}
              onServerToggle={onServerToggle}
              serverBusy={serverBusy}
            />
          ))}
        </div>
      ))}

      <div style={{
        marginTop: 32, padding: 16, background: '#fffbeb',
        border: '1px solid #fde68a', borderRadius: 8,
        fontSize: 12, color: '#78350f', lineHeight: 1.55,
      }}>
        <b>Tip:</b> toggling <code>Canvas V2</code> ON and reloading exposes
        the <a href="/canvas-v2" style={{ color: '#1e40af', fontWeight: 600 }}>/canvas-v2</a>{' '}
        route. Toggling it OFF again unregisters the route — direct navigation
        falls back to the Overview page.
      </div>
    </V2PageShell>
  );
}
